import axios from 'axios';

import { defineStore } from 'pinia';
import { DeleteUserFCMDeviceToken } from 'src/stores/user/commands/DeleteUserFCMDeviceToken';
import { RegisterUserFCMDeviceToken } from 'src/stores/user/commands/RegisterUserFCMDeviceToken';
import { USER } from 'src/stores/user/localStorageKeys';
import {
	GetTheUserConfigurationAndEffectiveRightsQuery,
	GetTheUserDetailsOfTheAuthenticatedUserQuery,
} from 'src/stores/user/queries';

import {
	User,
	UserConfigurationAndEffectiveRights,
	UserConfigurationAndEffectiveRightsRunType,
	UserRunType,
} from 'src/models/user';

import { urlForInstance } from 'src/util/common';

import {
	GetTheUserConfigurationAndEffectiveRightsQueryCallBack,
	GetTheUserDetailsOfTheAuthenticatedUserQueryCallBack,
} from './callbacks';
import {
	DeleteUserFcmDeviceTokenPayload,
	FetchAuthenticatedUserInformationPayload,
	FetchTwoFactorAuthenticationIsEnabledPayload,
	FetchUserConfigurationAndEffectiveRightsPayload,
	RegisterUserFcmDeviceTokenPayload,
	TwoFactorAuthenticationIsEnabledResponse,
	UserStoreState,
} from './interfaces';
import { FcmDeviceTokenState } from './types';

const createDefaultState = (): UserStoreState => ({
	fcmDeviceToken: '',
	twoFactorAuthenticationIsEnabled: false,
	user: localStorage.getItem(USER) !== null ? (JSON.parse(localStorage.getItem(USER) as string) as User) : null,
	userConfigurationAndEffectiveRights: null,
	userConfigurationAndEffectiveRightsUnavailable: false,
});

export const useUserStore = defineStore('user', {
	actions: {
		async deleteUserFcmDeviceToken(payload: DeleteUserFcmDeviceTokenPayload): Promise<void> {
			await payload.commandBus
				?.sendCommand(new DeleteUserFCMDeviceToken(payload.fcmDeviceToken.value))
				?.then(() => {
					this.setFcmDeviceToken('');
				});
		},
		async fetchAuthenticatedUserInformation(payload: FetchAuthenticatedUserInformationPayload): Promise<void> {
			if (payload.queryStore !== undefined) {
				await payload.queryStore.query<User>({
					callback: new GetTheUserDetailsOfTheAuthenticatedUserQueryCallBack((user) =>
						this.setAuthenticatedUserInformation(user)
					),
					createTime: new Date(),
					query: new GetTheUserDetailsOfTheAuthenticatedUserQuery(),
					responseType: 'json',
					runType: UserRunType,
				});
				return;
			}

			this.setAuthenticatedUserInformation(null);
		},
		async fetchTwoFactorAuthenticationIsEnabled(
			payload: FetchTwoFactorAuthenticationIsEnabledPayload
		): Promise<void> {
			const url = await urlForInstance(payload.instance.toLowerCase());
			const response = await axios.get<TwoFactorAuthenticationIsEnabledResponse>(
				`${url}/authentication/login/two-factor-authentication-is-enabled/format/json`,
				{
					params: {
						username: payload.username,
					},
				}
			);

			this.setTwoFactorAuthenticationIsEnabled(response.data.twoFactorAuthenticationIsEnabled);
		},
		async fetchUserConfigurationAndEffectiveRights(
			payload: FetchUserConfigurationAndEffectiveRightsPayload
		): Promise<void> {
			if (payload.queryStore !== undefined) {
				await payload.queryStore.query<UserConfigurationAndEffectiveRights>({
					callback: new GetTheUserConfigurationAndEffectiveRightsQueryCallBack(
						(userConfigurationAndEffectiveRights, userConfigurationAndEffectiveRightsUnavailable = false) =>
							this.setUserConfigurationAndEffectiveRights(
								userConfigurationAndEffectiveRights,
								userConfigurationAndEffectiveRightsUnavailable
							)
					),
					createTime: new Date(),
					query: new GetTheUserConfigurationAndEffectiveRightsQuery(),
					responseType: 'json',
					runType: UserConfigurationAndEffectiveRightsRunType,
				});
				return;
			}

			this.setUserConfigurationAndEffectiveRights(null);
		},
		registerFcmDeviceToken(payload: RegisterUserFcmDeviceTokenPayload): void {
			void payload.commandBus
				?.sendCommand(new RegisterUserFCMDeviceToken(payload.fcmDeviceToken.value))
				?.then(() => {
					this.setFcmDeviceToken(payload.fcmDeviceToken);
				});
		},
		setAuthenticatedUserInformation(payload: User | null): void {
			this.user = payload;
			localStorage.setItem(USER, JSON.stringify(payload));
		},
		setFcmDeviceToken(payload: FcmDeviceTokenState): void {
			this.fcmDeviceToken = payload;
		},
		setTwoFactorAuthenticationIsEnabled(payload: boolean): void {
			this.twoFactorAuthenticationIsEnabled = payload;
		},
		setUserConfigurationAndEffectiveRights(
			payload: UserConfigurationAndEffectiveRights | null,
			unavailable = false
		): void {
			this.userConfigurationAndEffectiveRights = payload;
			this.userConfigurationAndEffectiveRightsUnavailable = unavailable;
		},
	},
	getters: {
		authenticatedUserInformation(state): User | null {
			return state.user;
		},
		isTwoFactorAuthenticationEnabled(state): boolean {
			return state.twoFactorAuthenticationIsEnabled;
		},
	},
	state: createDefaultState,
});
export * from './interfaces';
export * from './types';
